/**
 * AGP bespoke storefront chrome — 2026 redesign handoff.
 *
 * The handoff drew a pure CORPORATE site: a green markets bar, a white sticky nav
 * with a gold active underline, and no search/cart anywhere. AGP still sells
 * online, so we keep two commerce affordances the handoff omitted — a cart badge
 * and a login/account link — styled into the handoff's own chrome. Everything
 * else is faithful.
 *
 * The handoff was also desktop-only. Below 900px the markets bar and the six-item
 * nav row are dropped for a phone header (logo · cart · login · menu) and the nav
 * moves into a full-screen drawer; the markets list rides along at the foot of it.
 *
 * The SHARED <Header/> (search + cart + account) is deliberately NOT rendered
 * here. It still wraps the transactional routes (cart, checkout, product detail),
 * which live outside this shell, so the shop flow is untouched.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, Search, ShoppingCart, X } from 'lucide-react';
import { AGP_SITE } from '@mlm/shared';
import { useCartStore, cartCount } from '../../../storefront/lib/cart-store';
import { useAuthStore } from '../../../lib/auth-store';
import { useSiteContent } from '../../../storefront/lib/site-content';
import { AgpFooter } from './footer';
import { LOGO } from './tokens';
import './agp.css';

export type AgpPageKey = 'home' | 'products' | 'about' | 'testimonials' | 'policies' | 'contact';

/**
 * Handoff nav. The PATHS are unchanged from AGP v1 — existing links, TENANT_ROUTES
 * and the seeded layout all keep working. Only the LABELS are the redesign's:
 * "Products" -> "Product", "About Us" -> "Company", "Contact" -> "Contact Us".
 */
const NAV: { key: AgpPageKey; label: string; href: string }[] = [
  { key: 'home', label: 'Home', href: '/' },
  { key: 'products', label: 'Product', href: '/storefront' },
  { key: 'about', label: 'Company', href: '/about-us' },
  { key: 'testimonials', label: 'Testimonials', href: '/testimonials' },
  { key: 'policies', label: 'Policies', href: '/policies' },
  { key: 'contact', label: 'Contact Us', href: '/contact' },
];

export function AgpShell({ active, children }: { active: AgpPageKey; children: ReactNode }) {
  const COUNTRIES = useSiteContent(AGP_SITE).paras('chrome.topbar.countries');
  const count = useCartStore((s) => cartCount(s.lines));
  const authed = !!useAuthStore((s) => s.token);
  const [menuOpen, setMenuOpen] = useState(false);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');

  const accountHref = authed ? '/dashboard' : '/login';
  const accountLabel = authed ? 'My Account' : 'Login';

  // Product search — the shared /search results page + `/storefront/products?search=`
  // backend already work for every tenant; the bespoke shell just needs an entry
  // point. Empty submit → home (mirrors the shared header's runSearch).
  const runSearch = () => {
    const q = search.trim();
    navigate(q ? `/search?q=${encodeURIComponent(q)}` : '/');
    setMenuOpen(false);
  };

  // Any navigation closes the drawer — covers the links inside it and any other
  // route change (browser back, a redirect) that would otherwise leave it open.
  useEffect(() => setMenuOpen(false), [pathname]);

  // While the drawer is up it owns the viewport: Escape closes it, and the page
  // behind it must not scroll.
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  return (
    <div className="agp-site">
      {/* Top bar — the six AGP markets, account on the right. Desktop only; on
          phones the markets move into the drawer and account into the nav row. */}
      <div className="agp-topbar">
        <div className="agp-countries">
          {COUNTRIES.map((c, i) => (
            <span key={c} className={i === 0 ? 'active' : undefined}>
              {c}
            </span>
          ))}
        </div>
        <div className="agp-topbar-actions">
          <Link className="agp-topbar-link" to={accountHref}>
            {accountLabel}
          </Link>
        </div>
      </div>

      {/* Sticky nav. */}
      <nav className="agp-nav">
        <Link className="agp-wordmark" to="/">
          <img src={LOGO} alt="" />
          <span className="agp-wordmark-text">
            Alpha <span>Global</span> Prestige
          </span>
        </Link>

        <div className="agp-nav-links">
          {NAV.map((n) => (
            <Link key={n.key} to={n.href} className={active === n.key ? 'active' : undefined}>
              {n.label}
            </Link>
          ))}
        </div>

        <div className="agp-nav-actions">
          {/* Not in the handoff — see the file header. */}
          <form
            className="agp-search"
            role="search"
            onSubmit={(e) => {
              e.preventDefault();
              runSearch();
            }}
          >
            <Search size={16} strokeWidth={1.8} aria-hidden />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search products"
              aria-label="Search products"
            />
          </form>
          <Link
            className="agp-cart"
            to="/cart"
            aria-label={`Cart, ${count} item${count === 1 ? '' : 's'}`}
          >
            <ShoppingCart size={19} strokeWidth={1.8} />
            {count > 0 && <span className="agp-cart-badge">{count > 99 ? '99+' : count}</span>}
          </Link>
          <Link className="agp-nav-login" to={accountHref}>
            {accountLabel}
          </Link>
          <Link className="agp-nav-cta" to="/contact">
            Get In Touch
          </Link>
          <button
            type="button"
            className="agp-menu-btn"
            aria-label="Open menu"
            aria-expanded={menuOpen}
            aria-controls="agp-mobile-menu"
            onClick={() => setMenuOpen(true)}
          >
            <Menu size={22} strokeWidth={1.8} />
          </button>
        </div>
      </nav>

      {/* Mobile nav drawer. Always mounted so it can fade; `hidden` while closed
          keeps its links out of the tab order and off screen readers. */}
      <div
        id="agp-mobile-menu"
        className={menuOpen ? 'agp-mobile-menu open' : 'agp-mobile-menu'}
        hidden={!menuOpen}
      >
        <div className="agp-mobile-menu-top">
          <button
            type="button"
            className="agp-menu-btn agp-menu-close"
            aria-label="Close menu"
            onClick={() => setMenuOpen(false)}
          >
            <X size={22} strokeWidth={1.8} />
          </button>
        </div>

        <form
          className="agp-search agp-search-mobile"
          role="search"
          onSubmit={(e) => {
            e.preventDefault();
            runSearch();
          }}
        >
          <Search size={16} strokeWidth={1.8} aria-hidden />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search products"
            aria-label="Search products"
          />
        </form>

        <div className="agp-mobile-links">
          {NAV.map((n) => (
            <Link key={n.key} to={n.href} className={active === n.key ? 'active' : undefined}>
              {n.label}
            </Link>
          ))}
        </div>

        <div className="agp-mobile-actions">
          <Link className="agp-btn agp-btn-primary" to="/contact">
            Get In Touch
          </Link>
          <Link className="agp-mobile-account" to={accountHref}>
            {accountLabel}
          </Link>
        </div>

        <div className="agp-mobile-countries">
          {COUNTRIES.map((c, i) => (
            <span key={c} className={i === 0 ? 'active' : undefined}>
              {c}
            </span>
          ))}
        </div>
      </div>

      <main>{children}</main>

      <AgpFooter />
    </div>
  );
}
